import type {AiLabelIcon, AiLabelPosition, AiLabelStyle} from './metadata/ai-label';
import type {CaptionSegment} from './metadata/captions';
import type {VolumePoint} from './metadata/envelope';

export type {CaptionSegment, VolumePoint};

export type ClipStyle = 'reel' | 'genericCarousel' | 'quote';

export interface MusicPayload {
  media_id: string;
  url?: string;
  filename?: string;
  metadata?: Record<string, unknown>;
}

export interface RendererPayload {
  clip_id: string;
  clip_style: string;
  metadata: Record<string, unknown>;
  music?: MusicPayload;
  narrator?: MusicPayload;
  media: {
    images: MediaItemPayload[];
    ai_videos: MediaItemPayload[];
    audio: MediaItemPayload[];
  };
}

export interface MediaItemPayload {
  id: string;
  filename: string;
  prompt: string;
  metadata?: Record<string, unknown>;
}

export type SlideMediaType = 'image' | 'video';

/**
 * Every transition the renderer can present, in the order the editor offers them.
 * The normalizer whitelist and the presentation resolver both derive from this list,
 * so a new name only has to be added here plus wired in `zoomPresentation.ts`.
 */
export const TRANSITION_NAMES = [
  'fade',
  'zoom-in',
  'zoom-out',
  'slow-zoom-in',
  'slow-zoom-out',
  'slide',
  'wipe',
  'flip',
  'clockWipe',
  'zoom-punch',
] as const;

export type TransitionName = (typeof TRANSITION_NAMES)[number];
export type SubtitleStyle = 'box' | 'highlight' | 'karaoke';

export interface ReelSlide {
  id: string;
  mediaType: SlideMediaType;
  path: string;
  text: string;
  textPosition: string;
  role: string;
  order: number;
  duration: string;
  transition?: TransitionName;
  /** Play this video slide's own audio track (resolved from per-slide metadata with the clip-level nativeVideoAudio default). */
  nativeAudio?: boolean;
  /** Seconds into the source video where playback starts (videos only). */
  trimStartSeconds?: number;
  /** Seconds into the source video where playback ends (videos only). */
  trimEndSeconds?: number;
  /** Overlap with the next slide, in seconds. Defaults to 0.3 (0.5 for `slow-*`). */
  transitionDurationSeconds?: number;
  /** Per-slide subtitle placement, canvas fractions 0..1 anchored at the block center. */
  subtitlePosX?: number;
  subtitlePosY?: number;
  subtitleScale?: number;
  /**
   * Native-audio envelope, `t` in seconds relative to THIS slide's start on the clip
   * timeline. Present only when the payload asks for the separated-audio path.
   */
  nativeAudioVolumePoints?: VolumePoint[];
  /** Seconds into the source video for the separated audio track; defaults to the video's own trim. */
  nativeAudioTrimStartSeconds?: number;
  nativeAudioTrimEndSeconds?: number;
}

/**
 * Narration-wave clip-level tracks. Every field is optional and omitted from the
 * normalized metadata unless the payload carries it, so a legacy payload keeps
 * producing a byte-identical `metadata.json`.
 */
export interface NarrationTracks {
  /** `show_subtitles=false`: silences segments, `subtitleText` AND per-slide text. */
  subtitlesHidden?: boolean;
  /** When non-empty this is the ONLY subtitle source (slide text + `subtitleText` are ignored). */
  captionSegments?: CaptionSegment[];
  /** Seconds of silence before the narrator starts on the clip timeline. */
  narratorOffsetSeconds?: number;
  /** Seconds into the narrator source file. */
  narratorTrimStartSeconds?: number;
  narratorTrimEndSeconds?: number;
  /** Narrator gain envelope, `t` in seconds on the clip timeline. */
  narratorVolumePoints?: VolumePoint[];
  musicOffsetSeconds?: number;
  musicTrimStartSeconds?: number;
  musicTrimEndSeconds?: number;
  /** Music gain envelope; when present it REPLACES the 0.3 duck heuristic. */
  musicVolumePoints?: VolumePoint[];
}

export interface ReelRenderMetadata extends NarrationTracks {
  clip_id: string;
  clip_style: 'reel';
  substyle: string;
  subtitleText: string;
  subtitleStyle: SubtitleStyle;
  totalDuration: string;
  musicUrl: string;
  musicOverridePath: string;
  narratorPath: string;
  narratorUrl: string;
  /** Clip-level default for playing the native audio of video slides. */
  nativeVideoAudio: boolean;
  /** Clip-level subtitle placement, canvas fractions 0..1 anchored at the block center. */
  subtitlePosX?: number;
  subtitlePosY?: number;
  subtitleScale?: number;
  /** Drifting text watermark; absent/empty = off. See `metadata/watermark.ts`. */
  watermarkText?: string;
  /** 0.05..0.5, default 0.22. Only meaningful together with `watermarkText`. */
  watermarkOpacity?: number;
  /** EU AI-transparency label; ON unless explicitly off. See `metadata/ai-label.ts`. */
  aiLabel: boolean;
  aiLabelIcon: AiLabelIcon;
  aiLabelStyle: AiLabelStyle;
  aiLabelPosition: AiLabelPosition;
  /** Backend-aggregated provenance summary, embedded into the mp4 tags. */
  aiModels?: string;
  slides: ReelSlide[];
}

export type QuoteOrientation = 'portrait' | 'landscape';

export interface QuoteRenderMetadata extends NarrationTracks {
  clip_id: string;
  clip_style: 'quote';
  orientation: QuoteOrientation;
  quoteText: string;
  quoteAttribution: string;
  quoteFont: string;
  /** Only emitted when caption segments exist — quote clips have no legacy subtitle track. */
  subtitleStyle?: SubtitleStyle;
  totalDuration: string;
  musicUrl: string;
  musicOverridePath: string;
  narratorPath: string;
  narratorUrl: string;
  nativeVideoAudio: boolean;
  /** Clip-level subtitle placement, canvas fractions 0..1 anchored at the block center. */
  subtitlePosX?: number;
  subtitlePosY?: number;
  subtitleScale?: number;
  /** Drifting text watermark; absent/empty = off. See `metadata/watermark.ts`. */
  watermarkText?: string;
  /** 0.05..0.5, default 0.22. Only meaningful together with `watermarkText`. */
  watermarkOpacity?: number;
  /** EU AI-transparency label; ON unless explicitly off. See `metadata/ai-label.ts`. */
  aiLabel: boolean;
  aiLabelIcon: AiLabelIcon;
  aiLabelStyle: AiLabelStyle;
  aiLabelPosition: AiLabelPosition;
  /** Backend-aggregated provenance summary, embedded into the mp4 tags. */
  aiModels?: string;
  slides: ReelSlide[];
}

export interface GenericCarouselImage {
  id: string;
  filename: string;
  path: string;
  prompt: string;
  order: number;
  title: string;
  explanation: string;
}

export interface GenericCarouselMedia {
  id: string;
  filename: string;
  path: string;
  prompt: string;
}

export interface GenericCarouselBrandPresetValues {
  layoutVariant?:
    | 'image-led'
    | 'poster'
    | 'no-image-typography'
    | 'chat-overlay'
    | 'split-comparison'
    | 'editorial-card';
  brandTag?: string;
  logoUrl?: string;
  highlightTextureUrl?: string;
  headlineFontId?: string;
  bodyFontId?: string;
  highlightStart?: string;
  highlightEnd?: string;
  highlightIntensity?: number;
  highlightTextureStrength?: number;
  bottomOverlayStrength?: number;
  textDensity?: 'dramatic' | 'balanced' | 'compact';
  showDecorativeLines?: boolean;
}

export interface GenericCarouselRenderMetadata {
  clip_id: string;
  clip_style: 'genericCarousel';
  brandingTag: string;
  headline: string;
  actionText: string;
  callToAction: string;
  stylePresetId?: string;
  stylePresetName?: string;
  stylePresetDirty?: boolean;
  stylePresetDraft?: GenericCarouselBrandPresetValues;
  images: GenericCarouselImage[];
  ai_videos: GenericCarouselMedia[];
  audio: GenericCarouselMedia[];
}

export interface GenericCarouselFrameProps extends GenericCarouselRenderMetadata {
  variant: 'headline' | 'slide';
  slideIndex: number;
}

export type RenderMetadata = ReelRenderMetadata | GenericCarouselRenderMetadata | QuoteRenderMetadata;

export interface UploadedFile {
  fieldname: string;
  originalname: string;
  buffer: Buffer;
}

export interface OutputFile {
  name: string;
  type: 'video' | 'image';
  data: string;
}

export interface RenderResponse {
  clip_id: string;
  files: OutputFile[];
}
